import { CenteredLayout } from '@/components/layouts/CenteredLayout';
import { Loadable } from '@/components/Loadable';
import PriceDisplay from '@/components/PriceDisplay';
import RefundConsents from '@/components/refund/RefundConsents';
import { useRefundDeepLink } from '@/hooks/useRefundDeepLink';
import { TransText } from '@/i18n/trans/text';
import RefundAmountSummary from '@/routes/refund/RefundAmountSummary';
import type {
  AdmissionDTO,
  BookedTripDTO,
  FeeDTO,
  PassengerDTO,
  RefundOffersDTO,
} from '@/types/dto/agent';
import { formatDateWithDayOfWeek, stripTimeZoneOffset } from '@/utils/dateTime';
import { getPassengerName } from '@/utils/passenger';
import { Button, Icons, Typography } from '@packages/webshop-design-system';
import { type FC, type ReactNode, useMemo, useState } from 'react';

// An offer exposes its fulfillment both as `fulfillmentId` (the first one) and
// as the `fulfillments` list; collect all of them so an admission can match by
// either.
const getRefundedFulfillmentIds = (
  refundOffers: RefundOffersDTO
): Set<string> =>
  new Set(
    (refundOffers.refundOffers ?? []).flatMap((offer) => [
      ...(offer.fulfillmentId ? [offer.fulfillmentId] : []),
      ...(offer.fulfillments ?? []).flatMap((f) => (f.id ? [f.id] : [])),
    ])
  );

const isRefunded = (
  fulfillments: Array<{ id?: string }> | undefined,
  refundedIds: Set<string>
): boolean => !!fulfillments?.some((f) => f.id && refundedIds.has(f.id));

interface RefundedTrip {
  trip: BookedTripDTO;
  admissions: Array<AdmissionDTO>;
  // Booking/sales fees the refund covers. These live on the booked offer, not
  // on any admission, so without them the itemized list undershoots the
  // offer's refundable total.
  fees: Array<FeeDTO>;
}

// Keeps only the admissions and fees the refunded fulfillment(s) cover, grouped
// by trip. This is a full refund of the fulfillment, so a matched admission is
// refunded whole (ticket + its ancillaries).
const getRefundedTrips = (
  trips: Array<BookedTripDTO>,
  refundedIds: Set<string>
): Array<RefundedTrip> =>
  trips
    .map((trip) => {
      const bookedOffers = trip.bookedOffers ?? [];

      return {
        trip,
        admissions: bookedOffers
          .flatMap((offer) => offer.admissions ?? [])
          .filter((admission) =>
            isRefunded(admission.fulfillments, refundedIds)
          ),
        fees: bookedOffers
          .flatMap((offer) => offer.fees ?? [])
          .filter((fee) => isRefunded(fee.fulfillments, refundedIds)),
      };
    })
    .filter(({ admissions, fees }) => admissions.length > 0 || fees.length > 0);

const RefundOverview: FC = () => {
  const { refundOffers, booking, pending, confirm, cancel } =
    useRefundDeepLink();
  const [consentsAccepted, setConsentsAccepted] = useState(false);

  const passengerMap: PassengerMap = useMemo(
    () =>
      new Map(
        booking?.passengers?.map((passenger, index) => [
          passenger.id,
          { passenger, index },
        ]) ?? []
      ),
    [booking]
  );

  const refundedTrips = useMemo(
    () =>
      refundOffers
        ? getRefundedTrips(
            booking?.bookedTrips ?? [],
            getRefundedFulfillmentIds(refundOffers)
          )
        : [],
    [booking, refundOffers]
  );

  if (!refundOffers) {
    return (
      <CenteredLayout>
        <Loadable loading className={{ regular: 'my-10' }}>
          {null}
        </Loadable>
      </CenteredLayout>
    );
  }

  return (
    <CenteredLayout>
      <div className="mx-auto my-10 flex w-full max-w-tablet flex-col rounded-lg border border-neutral-light p-4">
        {refundedTrips.length > 0 && (
          <Typography variant="subtitle" className="mb-4">
            <TransText
              i18nKey="refundTrips"
              values={{ count: refundedTrips.length }}
            />
          </Typography>
        )}

        <RefundAmountSummary refundOffers={refundOffers} />

        {refundedTrips.length > 0 && (
          <>
            <Typography variant="subtitle" className="my-5">
              <TransText i18nKey="youAreAboutToRefundFollowingItems" />
            </Typography>
            <div className="flex flex-col gap-3">
              {refundedTrips.map(({ trip, admissions, fees }) => (
                <TripSection
                  key={trip.id}
                  trip={trip}
                  admissions={admissions}
                  fees={fees}
                  passengerMap={passengerMap}
                />
              ))}
            </div>
          </>
        )}

        <div className="mt-6">
          <RefundConsents onAcceptanceChange={setConsentsAccepted} />
        </div>

        <div className="mt-4 flex gap-2 laptop:justify-end">
          <Button
            data-testid="cancel-refund-btn"
            variant="secondary"
            onClick={cancel}
            isDisabled={pending === 'confirm'}
            isPending={pending === 'cancel'}
            className="rounded-lg laptop:w-auto"
          >
            <TransText i18nKey="cancel" />
          </Button>
          <Button
            data-testid="confirm-refund-btn"
            onClick={confirm}
            isDisabled={!consentsAccepted || pending === 'cancel'}
            isPending={pending === 'confirm'}
            className="rounded-lg laptop:w-auto"
            startAdornment={<Icons.ticketReturn height={16} width={16} />}
          >
            <TransText i18nKey="confirm" />
          </Button>
        </div>
      </div>
    </CenteredLayout>
  );
};

// Booked passengers keyed by id, carrying their booking position so an unnamed
// passenger falls back to the right "Passenger N" label.
type PassengerMap = Map<
  string | undefined,
  { passenger: PassengerDTO; index: number }
>;

const getPassengerLabel = (
  passengerIds: Array<string> | undefined,
  passengerMap: PassengerMap
): ReactNode => {
  const entry = passengerMap.get(passengerIds?.[0]);

  return (
    entry &&
    getPassengerName(
      {
        firstName: entry.passenger.firstName,
        lastName: entry.passenger.lastName,
      },
      entry.index
    )
  );
};

const RefundItem: FC<{
  icon: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  amount: number | undefined;
}> = ({ icon, title, subtitle, amount }) => (
  <div className="flex flex-row items-stretch justify-between">
    <div className="flex flex-row items-start gap-2">
      <div className="flex h-5 w-5 items-center justify-center">{icon}</div>
      <div>
        <Typography variant="body1-bold">{title}</Typography>
        {subtitle && (
          <Typography variant="body2" className="text-neutral">
            {subtitle}
          </Typography>
        )}
      </div>
    </div>
    <Typography variant="body2-bold" className="mt-[2px] text-primary">
      <PriceDisplay amount={amount} />
    </Typography>
  </div>
);

const TripSection: FC<{
  trip: BookedTripDTO;
  admissions: Array<AdmissionDTO>;
  fees: Array<FeeDTO>;
  passengerMap: PassengerMap;
}> = ({ trip, admissions, fees, passengerMap }) => (
  <div className="rounded-md border border-neutral-light">
    <div className="flex items-center justify-between rounded-t-md border-b border-neutral-light bg-primary-pale px-4 py-3">
      <div className="flex flex-row items-center gap-2">
        <Icons.transportFrom height={16} width={16} className="text-primary" />
        <Typography variant="body1-bold">
          <TransText
            i18nKey={trip.isOutbound ? 'outboundTrips' : 'returnTrips'}
            values={{ count: 1 }}
          />
        </Typography>
      </div>
      <Typography variant="body2" className="text-neutral">
        {trip.departureTime &&
          formatDateWithDayOfWeek(stripTimeZoneOffset(trip.departureTime))}
      </Typography>
    </div>
    <div className="px-4 py-3">
      <div className="flex flex-row items-center gap-1">
        <Typography variant="body1-bold">{trip.originStop?.name}</Typography>
        <Icons.oneWayAlt height={16} width={16} />
        <Typography variant="body1-bold">
          {trip.destinationStop?.name}
        </Typography>
      </div>
      <div className="mt-3 flex flex-col gap-3">
        {admissions.map((admission) => {
          const passengerName = getPassengerLabel(
            admission.passengerIds,
            passengerMap
          );

          return (
            <div key={admission.id} className="flex flex-col gap-3">
              <RefundItem
                icon={
                  <Icons.ticket
                    height={16}
                    width={16}
                    className="text-primary"
                  />
                }
                title={admission.productSummary || admission.summary}
                subtitle={passengerName}
                amount={admission.price?.amount}
              />
              {admission.ancillaries
                ?.filter((ancillary) => ancillary.status !== 'REFUNDED')
                .map((ancillary) => (
                  <RefundItem
                    key={ancillary.id}
                    icon={
                      <Icons.addon
                        height={16}
                        width={16}
                        className="text-primary"
                      />
                    }
                    title={
                      ancillary.productDescription || ancillary.productSummary
                    }
                    subtitle={getPassengerLabel(
                      ancillary.passengerIds ?? admission.passengerIds,
                      passengerMap
                    )}
                    amount={ancillary.price?.amount}
                  />
                ))}
            </div>
          );
        })}
        {fees.map((fee) => (
          <RefundItem
            key={fee.id}
            icon={
              <Icons.coin height={16} width={16} className="text-primary" />
            }
            title={<TransText i18nKey={fee.type ?? 'SALES_FEE'} />}
            amount={fee.price?.amount}
          />
        ))}
      </div>
    </div>
  </div>
);

export default RefundOverview;
